import { Audited, BaseEntity, ID } from '@ianesthesie-kit/services';
import { GroupedList } from '@/util';
import {
  HasEmphasis,
  isSourceTypeTranslatable,
  isUserInput,
  MedicalComponent
} from '@/modules/surgicalEvent';
import { SnomedCode } from '@/modules/snomed';

export enum MedicalHoItemCategory {
  SURGICAL = 'SURGICAL',
  ANESTHESIA = 'ANESTHESIA',
  CARDIO_VASCULAR = 'CARDIO_VASCULAR',
  PULMONARY = 'PULMONARY',
  NEURO = 'NEURO',
  GI = 'GI',
  RENAL = 'RENAL',
  ENDOCRINE = 'ENDOCRINE',
  HEMATO = 'HEMATO',
  ENT = 'ENT',
  GYN = 'GYN',
  OBSTETRIC = 'OBSTETRIC',
  FAMILY = 'FAMILY',
  GENERAL = 'GENERAL',
  BILLING = 'BILLING'
}

export enum MedicalHoItemType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  CHOICE = 'CHOICE'
}

export const appliesToFemales = (
  c: MedicalHoItemCategory | undefined
): boolean => !!c;

export const appliesToMales = (c: MedicalHoItemCategory | undefined): boolean =>
  !!c &&
  c !== MedicalHoItemCategory.GYN &&
  c !== MedicalHoItemCategory.OBSTETRIC;

export interface MedicalHoItem extends MedicalComponent, HasEmphasis, Audited {
  code: string;
  translationCode: string;
  category: MedicalHoItemCategory;
  qcode?: string;
  translationQcode?: string;
  snomedCode?: SnomedCode;
  icd10Code?: string;
  isScore?: boolean | 'notInteractive';
  type?: MedicalHoItemType; //TODO rename to qtype
  isApproximateDate?: boolean | 'yearOnly';
  scratchConsultationCleared?: boolean;
  notes?: string;
  mergedWith?: MedicalHoItem[];
  /**
   * List of IDs of items that were deleted because of merging. This is to add these IDs when a merged item is deleted
   * For example: if Smoker.Smoker is present, Smoker.Smoker is removed in place (frontend only) and all the relevant
   * smoker mhos are merged under SmokerWantToStop. If SmokerWantToStop is deleted, we need to delete Smoker.Smoker as well
   */
  removedWhenMergedIds?: ID[];
}

export const isTranslatable = (item: MedicalHoItem) => {
  // dirt alert
  if (!item.qcode && item.code?.startsWith('METS_')) {
    return true;
  }
  return (
    item.type === MedicalHoItemType.CHOICE &&
    isSourceTypeTranslatable(item.sourceType)
  );
};

export const needsQuestionTranslation = (item: MedicalHoItem) => {
  if (!item?.type || !item.translationQcode) {
    return false;
  }

  if (item.code === 'IDK') {
    return true;
  }

  if (item.type === MedicalHoItemType.CHOICE) {
    // if more exclusions like this are needed, consider a new solution
    if (
      item.qcode === 'PatientSatisfaction' ||
      item.qcode === 'LegalGuardian'
    ) {
      return true;
    }
    return (
      isUserInput(item.sourceType) &&
      // if more exclusions like this are needed, consider a new solution
      item.qcode !== 'AnesthesiaComplications'
    );
  }

  return true;
};

export type MedicalHo = BaseEntity &
  GroupedList<MedicalHoItem, MedicalHoItemCategory> & {
    /** Answers hidden for display, kept with the cached history for duplicate checks. */
    hiddenAnswers?: { items: MedicalHoItem[] };
  };
